import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { today, addDays, inr } from "@/lib/format";

export default function DocumentForm({ kind }) {
  const label = kind === "INV" ? "Invoice" : "Purchase Order";
  const partyKind = kind === "INV" ? "customer" : "supplier";
  const nav = useNavigate();
  const { company } = useAuth();

  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [terms, setTerms] = useState([]);
  const [pickedTerm, setPickedTerm] = useState("");

  const [form, setForm] = useState({
    party_id: "",
    date: today(),
    due_date: addDays(today(), 30),
    reference: "",
    notes: "",
    interstate: false,
    items: [],
    payment_terms: "",
    shipping_terms: "",
    quotation_ref: "",
    quotation_date: "",
    special_instructions: "",
    ship_to_option: "asmita",
  });

  // Pre-fill PO defaults from company config once loaded
  useEffect(() => {
    if (kind === "PO" && company?.po_defaults) {
      setForm((f) => ({
        ...f,
        payment_terms: f.payment_terms || company.po_defaults.payment_terms || "",
        shipping_terms: f.shipping_terms || company.po_defaults.shipping_terms || "",
        special_instructions: f.special_instructions || company.po_defaults.instructions || "",
      }));
    }
    // eslint-disable-next-line
  }, [company, kind]);

  useEffect(() => {
    api.get("/parties", { params: { kind: partyKind } }).then(({ data }) => setParties(data));
    api.get("/items").then(({ data }) => setItems(data));
    if (kind === "PO") {
      api.get("/terms").then(({ data }) => {
        setTerms(data);
        const def = data.find((t) => t.is_default);
        if (def) {
          setPickedTerm(def.term_id);
          setForm((f) => f.special_instructions ? f : { ...f, special_instructions: def.content });
        }
      });
    }
  }, [partyKind, kind]);

  useEffect(() => {
    if (!form.party_id) return setPriceList([]);
    api.get(`/pricelist/${form.party_id}`).then(({ data }) => setPriceList(data));
    const party = parties.find((p) => p.party_id === form.party_id);
    if (party?.credit_days) {
      setForm((f) => ({ ...f, due_date: addDays(f.date, party.credit_days) }));
    }
  }, [form.party_id, parties]);

  const priceFor = (item_id) => priceList.find((p) => p.item_id === item_id)?.price;

  const addLine = (item_id) => {
    const it = items.find((i) => i.item_id === item_id);
    if (!it) return;
    const rate = priceFor(item_id) ?? 0;
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          item_id: it.item_id,
          item_name: it.name,
          hsn: it.hsn || "",
          unit: it.unit,
          quantity: 1,
          rate,
          gst_rate: it.gst_rate || 18,
        },
      ],
    }));
  };

  const updateLine = (idx, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };
  const removeLine = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    for (const l of form.items) {
      const amt = Number(l.quantity || 0) * Number(l.rate || 0);
      sub += amt;
      const g = (amt * Number(l.gst_rate || 0)) / 100;
      if (form.interstate) igst += g;
      else { cgst += g / 2; sgst += g / 2; }
    }
    const total = sub + cgst + sgst + igst;
    return { sub, cgst, sgst, igst, total };
  }, [form.items, form.interstate]);

  const submit = async () => {
    if (!form.party_id) return toast.error("Select a party");
    if (form.items.length === 0) return toast.error("Add at least one item");
    try {
      const url = kind === "INV" ? "/invoices" : "/purchase-orders";
      const { data } = await api.post(url, form);
      toast.success(`${label} ${data.number} created`);
      nav(`/documents/${data.doc_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title={`New ${label}`}
        subtitle={kind === "INV" ? "Bill your customer with GST breakdown" : "Order goods from your supplier"}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav(-1)}>Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-document-btn">
              Save {label}
            </Button>
          </div>
        }
      />
      <div className="px-8 py-6 space-y-6">
        <Card className="p-6 border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-600">{partyKind === "customer" ? "Customer" : "Supplier"}</Label>
              <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                <SelectTrigger data-testid="doc-party-select"><SelectValue placeholder={`Select ${partyKind}`} /></SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.party_id} value={p.party_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="PO/quote/etc." />
            </div>
            <div className="space-y-1.5 flex flex-col justify-end">
              <Label className="text-xs text-slate-600">Inter-state (IGST)</Label>
              <div className="flex items-center gap-3 pt-1">
                <Switch checked={form.interstate} onCheckedChange={(v) => setForm({ ...form, interstate: v })} />
                <span className="text-sm text-slate-600">{form.interstate ? "IGST" : "CGST + SGST"}</span>
              </div>
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-600">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {kind === "PO" && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h4 className="font-display font-bold text-slate-800 mb-3">PO Terms & Instructions</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Quotation Ref</Label>
                  <Input value={form.quotation_ref} onChange={(e) => setForm({ ...form, quotation_ref: e.target.value })} placeholder="e.g. Q-2026-45" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Quotation Date</Label>
                  <Input type="date" value={form.quotation_date} onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Payment Terms</Label>
                  <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. 45 days net" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Shipping Terms</Label>
                  <Input value={form.shipping_terms} onChange={(e) => setForm({ ...form, shipping_terms: e.target.value })} placeholder="e.g. Ex. Uchgaon, Kolhapur" />
                </div>
                <div className="md:col-span-4 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-600">Comments / Special Instructions</Label>
                    {terms.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">Load from library</span>
                        <Select value={pickedTerm} onValueChange={(v) => {
                          setPickedTerm(v);
                          const t = terms.find((x) => x.term_id === v);
                          if (t) setForm((f) => ({ ...f, special_instructions: t.content }));
                        }}>
                          <SelectTrigger className="h-8 w-56" data-testid="terms-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
                          <SelectContent>
                            {terms.map((t) => (
                              <SelectItem key={t.term_id} value={t.term_id}>{t.name}{t.is_default ? " (default)" : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <Textarea
                    rows={4}
                    value={form.special_instructions}
                    onChange={(e) => setForm({ ...form, special_instructions: e.target.value })}
                    data-testid="special-instructions"
                  />
                </div>
              </div>

              {company?.po_ship_to?.name && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Ship To
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    <ShipCard
                      selected={form.ship_to_option === "asmita"}
                      onClick={() => setForm({ ...form, ship_to_option: "asmita" })}
                      title={company.po_ship_to.name}
                      lines={[
                        company.po_ship_to.address,
                        company.po_ship_to.gstin ? `GSTN: ${company.po_ship_to.gstin}` : "",
                        company.po_ship_to.contact ? `Contact: ${company.po_ship_to.contact}` : "",
                      ]}
                    />
                    <ShipCard
                      selected={form.ship_to_option === "self"}
                      onClick={() => setForm({ ...form, ship_to_option: "self" })}
                      title={company.name}
                      lines={[
                        company.address,
                        company.phone ? `Phone: ${company.phone}` : "",
                        company.gstin ? `GSTIN: ${company.gstin}` : "",
                      ]}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold">Line Items</h3>
            <div className="flex items-center gap-2">
              <Select value="" onValueChange={addLine}>
                <SelectTrigger className="w-72" data-testid="add-line-item-select">
                  <SelectValue placeholder="+ Add item to bill" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((i) => (
                    <SelectItem key={i.item_id} value={i.item_id}>
                      {i.sku ? `[${i.sku}] ` : ""}{i.name}
                      {i.hsn ? ` · HSN ${i.hsn}` : ""}
                      {priceFor(i.item_id) != null ? ` — ₹${priceFor(i.item_id)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">GST%</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Add items above.</TableCell></TableRow>
              )}
              {form.items.map((l, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{l.item_name}</TableCell>
                  <TableCell><Input className="w-24" value={l.hsn} onChange={(e) => updateLine(i, { hsn: e.target.value })} /></TableCell>
                  <TableCell className="text-right"><Input type="number" className="w-24 ml-auto text-right" value={l.quantity} onChange={(e) => updateLine(i, { quantity: +e.target.value })} /></TableCell>
                  <TableCell className="text-right"><Input type="number" className="w-28 ml-auto text-right" value={l.rate} onChange={(e) => updateLine(i, { rate: +e.target.value })} /></TableCell>
                  <TableCell className="text-right"><Input type="number" className="w-20 ml-auto text-right" value={l.gst_rate} onChange={(e) => updateLine(i, { gst_rate: +e.target.value })} /></TableCell>
                  <TableCell className="text-right number-cell font-semibold">{inr((l.quantity || 0) * (l.rate || 0))}</TableCell>
                  <TableCell><Button variant="ghost" size="icon" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-rose-500" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end mt-6">
            <div className="w-full max-w-sm space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="number-cell">{inr(totals.sub)}</span></div>
              {form.interstate ? (
                <div className="flex justify-between"><span className="text-slate-500">IGST</span><span className="number-cell">{inr(totals.igst)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-slate-500">CGST</span><span className="number-cell">{inr(totals.cgst)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">SGST</span><span className="number-cell">{inr(totals.sgst)}</span></div>
                </>
              )}
              <div className="flex justify-between text-base font-display font-bold pt-2 border-t border-slate-200">
                <span>Total</span><span className="number-cell">{inr(totals.total)}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ShipCard({ selected, onClick, title, lines }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left flex-1 min-w-[220px] rounded-md border p-3 text-sm transition-colors ${
        selected
          ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="font-semibold text-slate-900">{title}</div>
      {lines.filter(Boolean).map((l, i) => (
        <div key={i} className="text-xs text-slate-600 whitespace-pre-line">{l}</div>
      ))}
    </button>
  );
}
